import { createListenerMiddleware } from '@reduxjs/toolkit';
import { formatDate } from '@ui/src';
import { getAmlGeneralInfo, getAmlGeneralInfoSuccess } from '../slices/aml/aml';
import {
  getCards,
  getCardsSuccess,
  onCardStatusChange,
  onCardStatusChangeSuccess,
  onSubmitCard,
  onSubmitCardSuccess,
  getCardInfo,
  getCardInfoSuccess,
} from '../slices/cards/cards';
import {
  getRecentActivity,
  getRecentActivitySuccess,
} from '../slices/overview/overview';
import {
  CardStatus,
  CustomerCard,
  CardType,
  CardPaymentSystem,
} from '../../../customers/types';
import {
  IndividualAmlGeneralInfo,
  IndividualCustomerRecentActivity,
} from '../../types';
import getCardAvailableStatuses from '../../../../helpers/get-card-available-statuses';
import { CustomersRootState } from '../../../../store/state';

const amlGeneralInfoMock: IndividualAmlGeneralInfo = {
  sector:
    'Travel agency, tour operator, reservation services and related activities',
  customerComment:
    '«I own a travel agency. I develop routes, accompany all customer registration, provide my own guides. The goal is complete protection and vivid emotions in the customer`s journey.»',
  nextReviewDate: new Date('2022-08-10').toISOString(),
};

const individualCustomerRecentActivityMock: IndividualCustomerRecentActivity[] =
  [
    {
      id: 'uuid-1',
      user: 'Christian Evans',
      operationDate: new Date().toISOString(),
      typeOfOperation: 'Account blocking on credit',
      ipAddess: '192.81.169.246',
      typeOfDevice: 'Win 10.11.1',
    },
    {
      id: 'uuid-2',
      user: 'Christian Evans',
      operationDate: new Date().toISOString(),
      typeOfOperation: 'Downloading statement',
      ipAddess: '192.81.169.246',
      typeOfDevice: 'Win 10.11.1',
    },
    {
      id: 'uuid-3',
      user: 'Christian Evans',
      operationDate: new Date().toISOString(),
      typeOfOperation: 'Changing single transaction amount',
      ipAddess: '192.81.169.246',
      typeOfDevice: 'Win 10.11.1',
    },
    {
      id: 'uuid-4',
      user: 'Christian Evans',
      operationDate: new Date().toISOString(),
      typeOfOperation: 'Add comment in AML & KYC',
      ipAddess: '192.81.169.246',
      typeOfDevice: 'Win 10.11.1',
    },
  ];

const individualCustomerCardsMock: CustomerCard[] = [
  {
    id: 'uuid-1',
    paymentSystem: CardPaymentSystem.VISA,
    number: '**57 8478',
    type: CardType.DEBIT,
    expiredDate: new Date('2028-01-04').toISOString(),
    balance: '587.54',
    balanceCurrency: 'eur',
    status: CardStatus.NEW,
    customerAccountId: '3.0.00.000001',
    address: 'United Kingdom, London, Apt. 203, 23, Baker street, W1 5DE',
    customerFirstName: 'James',
    customerLastName: 'Bond',
    virtual: false,
  },
  {
    id: 'uuid-2',
    paymentSystem: CardPaymentSystem.MASTER_CARD,
    number: '**57 8478',
    type: CardType.DEBIT,
    expiredDate: new Date('2028-01-04').toISOString(),
    balance: undefined,
    balanceCurrency: 'eur',
    status: CardStatus.ORDERED,
    customerAccountId: '3.0.00.000001',
    address: 'United Kingdom, London, Apt. 203, 23, Baker street, W1 5DE',
    customerFirstName: 'James',
    customerLastName: 'Bond',
    virtual: true,
  },
  {
    id: 'uuid-3',
    paymentSystem: CardPaymentSystem.MASTER_CARD,
    number: '**57 8478',
    type: CardType.DEBIT,
    expiredDate: new Date('2028-01-04').toISOString(),
    balance: '587.54',
    balanceCurrency: 'eur',
    status: CardStatus.ACTIVE,
    customerAccountId: '3.0.00.000001',
    address: 'United Kingdom, London, Apt. 203, 23, Baker street, W1 5DE',
    customerFirstName: 'James',
    customerLastName: 'Bond',
    virtual: false,
  },
  {
    id: 'uuid-4',
    paymentSystem: CardPaymentSystem.VISA,
    number: '**57 8478',
    type: CardType.DEBIT,
    expiredDate: new Date('2028-01-04').toISOString(),
    balance: '587.54',
    balanceCurrency: 'eur',
    status: CardStatus.FROZEN,
    customerAccountId: '3.0.00.000001',
    address: 'United Kingdom, London, Apt. 203, 23, Baker street, W1 5DE',
    customerFirstName: 'James',
    customerLastName: 'Bond',
    virtual: false,
  },
  {
    id: 'uuid-5',
    paymentSystem: CardPaymentSystem.VISA,
    number: '**57 8478',
    type: CardType.DEBIT,
    expiredDate: new Date('2028-01-04').toISOString(),
    balance: '587.54',
    balanceCurrency: 'eur',
    status: CardStatus.CANCELED,
    customerAccountId: '3.0.00.000001',
    address: 'United Kingdom, London, Apt. 203, 23, Baker street, W1 5DE',
    customerFirstName: 'James',
    customerLastName: 'Bond',
    virtual: false,
  },
  {
    id: 'uuid-6',
    paymentSystem: CardPaymentSystem.VISA,
    number: '**57 8478',
    type: CardType.DEBIT,
    expiredDate: new Date('2028-01-04').toISOString(),
    balance: '587.54',
    balanceCurrency: 'eur',
    status: CardStatus.VOID,
    customerAccountId: '3.0.00.000001',
    address: 'United Kingdom, London, Apt. 203, 23, Baker street, W1 5DE',
    customerFirstName: 'James',
    customerLastName: 'Bond',
    virtual: false,
  },
  {
    id: 'uuid-7',
    paymentSystem: CardPaymentSystem.VISA,
    number: '**57 8478',
    type: CardType.CORP,
    expiredDate: new Date('2028-01-04').toISOString(),
    balance: '14547.95',
    balanceCurrency: 'eur',
    status: CardStatus.ACTIVE,
    customerAccountId: '3.0.00.000001',
    address: 'United Kingdom, London, Apt. 203, 23, Baker street, W1 5DE',
    customerFirstName: 'James',
    customerLastName: 'Bond',
    virtual: true,
  },
];

const individualCustomerMiddleware = createListenerMiddleware();

individualCustomerMiddleware.startListening({
  actionCreator: getAmlGeneralInfo,
  effect: async (action, listenerApi) => {
    setTimeout(() => {
      listenerApi.dispatch(getAmlGeneralInfoSuccess(amlGeneralInfoMock));
      listenerApi.cancelActiveListeners();
    }, 1000);
  },
});

individualCustomerMiddleware.startListening({
  actionCreator: getCards,
  effect: async (action, listenerApi) => {
    const nextCards = individualCustomerCardsMock.map((item) => {
      const result = { ...item };

      result.availableStatuses = getCardAvailableStatuses(item.status);

      return result;
    });

    setTimeout(() => {
      listenerApi.dispatch(getCardsSuccess(nextCards));
      listenerApi.cancelActiveListeners();
    }, 1000);
  },
});

individualCustomerMiddleware.startListening({
  actionCreator: onSubmitCard,
  effect: async (action, listenerApi) => {
    const {
      individualCustomers: {
        info: {
          cards: { cards, orderCardFormValues },
        },
      },
    } = listenerApi.getState() as CustomersRootState;

    const nextCards = ([] as CustomerCard[]).concat(cards as CustomerCard[]);

    let card: CustomerCard = {
      id: `uuid-${nextCards.length + 1}`,
      paymentSystem: orderCardFormValues?.paymentSystem as CardPaymentSystem,
      number: '**57 8478',
      type: CardType.DEBIT,
      expiredDate: new Date('2028-01-04').toISOString(),
      balanceCurrency: 'eur',
      status: CardStatus.ORDERED,
      customerAccountId: '3.0.00.000001',
      address: '',
      customerFirstName: 'James',
      customerLastName: 'Bond',
      availableStatuses: getCardAvailableStatuses(CardStatus.ORDERED),
      virtual: !orderCardFormValues?.physical,
    };

    if (orderCardFormValues?.physical) {
      card = {
        ...card,
        address: `${orderCardFormValues.countryName}, ${orderCardFormValues.city}, ${orderCardFormValues.apartment}, ${orderCardFormValues.street}, ${orderCardFormValues.postCode}`,
        countryId: orderCardFormValues?.countryId,
        city: orderCardFormValues?.city,
        postCode: orderCardFormValues?.postCode,
        street: orderCardFormValues?.street,
        apartment: orderCardFormValues?.apartment,
      };
    }

    nextCards.push(card);

    setTimeout(() => {
      listenerApi.dispatch(onSubmitCardSuccess(nextCards));
      listenerApi.cancelActiveListeners();
    }, 1000);
  },
});

individualCustomerMiddleware.startListening({
  actionCreator: getCardInfo,
  effect: async (action, listenerApi) => {
    const {
      individualCustomers: {
        info: {
          cards: { cards, cardId },
        },
      },
    } = listenerApi.getState() as CustomersRootState;

    const currentCard = cards?.find((item) => item.id === cardId);

    setTimeout(() => {
      listenerApi.dispatch(getCardInfoSuccess(currentCard as CustomerCard));
      listenerApi.cancelActiveListeners();
    }, 1000);
  },
});

individualCustomerMiddleware.startListening({
  actionCreator: onCardStatusChange,
  effect: async (action, listenerApi) => {
    const {
      individualCustomers: {
        info: {
          cards: { cards },
        },
      },
    } = listenerApi.getState() as CustomersRootState;

    const nextCards = cards?.map((item) => {
      const result = { ...item };

      if (result.id === action.payload.id) {
        result.status = action.payload.status;
        result.availableStatuses = getCardAvailableStatuses(
          action.payload.status,
        );
      }

      return result;
    });

    setTimeout(() => {
      listenerApi.dispatch(
        onCardStatusChangeSuccess(nextCards as CustomerCard[]),
      );
      listenerApi.cancelActiveListeners();
    }, 1000);
  },
});

individualCustomerMiddleware.startListening({
  actionCreator: getRecentActivity,
  effect: async (action, listenerApi) => {
    const nextRecentActivity = individualCustomerRecentActivityMock.map(
      (item) => {
        const result = { ...item };

        result.operationDate = formatDate(result.operationDate, true);

        return result;
      },
    );

    setTimeout(() => {
      listenerApi.dispatch(getRecentActivitySuccess(nextRecentActivity));
      listenerApi.cancelActiveListeners();
    }, 1000);
  },
});

export default individualCustomerMiddleware;
